import { ApiError, type ScopeType } from '@fc/core'
import { Button, Label, Spinner } from '@fc/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldCheck, X } from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import {
  assignRole,
  listRoles,
  listScopes,
  type Member,
  type RoleSummary,
} from '../../lib/members-api'
import { SCOPE_LABEL } from './shared'

const selectClass =
  'h-10 w-full rounded-lg border border-border bg-card px-3 text-sm text-fg focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring disabled:opacity-50'

/**
 * Modal for granting a role to a member at a chosen scope. Roles the current
 * user is not allowed to delegate (privilege ceiling, server-derived) are shown
 * disabled — the API re-enforces this regardless.
 */
export function AssignRoleDialog({ member, onClose }: { member: Member; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [roleId, setRoleId] = useState('')
  const [scopeRefId, setScopeRefId] = useState('')

  const rolesQuery = useQuery({ queryKey: ['members', 'roles'], queryFn: listRoles })
  const scopesQuery = useQuery({ queryKey: ['members', 'scopes'], queryFn: listScopes })

  const selectedRole: RoleSummary | undefined = useMemo(
    () => rolesQuery.data?.find((r) => r.id === roleId),
    [rolesQuery.data, roleId],
  )
  const scopeType = (selectedRole?.scopeLevel ?? 'org') as ScopeType

  const scopeOptions = useMemo(() => {
    if (!scopesQuery.data) return []
    if (scopeType === 'site') return scopesQuery.data.sites
    if (scopeType === 'team') return scopesQuery.data.teams
    if (scopeType === 'group') return scopesQuery.data.groups
    return []
  }, [scopesQuery.data, scopeType])

  const mutation = useMutation({
    mutationFn: () =>
      assignRole(member.id, {
        roleId,
        scopeType,
        siteId: scopeType === 'site' ? scopeRefId : null,
        teamId: scopeType === 'team' ? scopeRefId : null,
        groupId: scopeType === 'group' ? scopeRefId : null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] })
      onClose()
    },
  })

  const needsScopeRef = scopeType !== 'org'
  const canSubmit = roleId !== '' && (!needsScopeRef || scopeRefId !== '') && !mutation.isPending

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (canSubmit) mutation.mutate()
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-role-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-border bg-surface p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h2 id="assign-role-title" className="font-display text-lg font-semibold">
              Assign a role
            </h2>
            <p className="text-sm text-muted">
              Grant {member.name} a role. You can only assign roles within your own permissions.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-dim hover:bg-card-hover hover:text-fg"
          >
            <X className="size-4" />
          </button>
        </div>

        {rolesQuery.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-muted">
            <Spinner /> Loading roles…
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="role">Role</Label>
              <select
                id="role"
                className={selectClass}
                value={roleId}
                onChange={(e) => {
                  setRoleId(e.target.value)
                  setScopeRefId('')
                }}
                required
              >
                <option value="">Select a role…</option>
                {rolesQuery.data?.map((r) => (
                  <option key={r.id} value={r.id} disabled={!r.delegable}>
                    {r.name}
                    {!r.delegable ? ' (beyond your permissions)' : ''}
                  </option>
                ))}
              </select>
              {selectedRole?.description && (
                <p className="text-xs text-dim">{selectedRole.description}</p>
              )}
            </div>

            {needsScopeRef && (
              <div className="space-y-1.5">
                <Label htmlFor="scope-ref">{SCOPE_LABEL[scopeType]}</Label>
                <select
                  id="scope-ref"
                  className={selectClass}
                  value={scopeRefId}
                  onChange={(e) => setScopeRefId(e.target.value)}
                  required
                  disabled={scopesQuery.isLoading}
                >
                  <option value="">Select a {SCOPE_LABEL[scopeType].toLowerCase()}…</option>
                  {scopeOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
                {scopeOptions.length === 0 && !scopesQuery.isLoading && (
                  <p className="text-xs text-dim">
                    No {SCOPE_LABEL[scopeType].toLowerCase()}s exist yet.
                  </p>
                )}
              </div>
            )}

            {selectedRole && !selectedRole.delegable && (
              <p className="flex items-center gap-1.5 text-xs text-warning">
                <ShieldCheck className="size-3.5" />
                This role exceeds your own permissions and can’t be granted by you.
              </p>
            )}

            {mutation.isError && (
              <p className="text-sm text-danger" role="alert">
                {mutation.error instanceof ApiError
                  ? mutation.error.message
                  : 'Could not assign the role.'}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {mutation.isPending ? <Spinner /> : null}
                Assign role
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
